import { useRef, useCallback } from 'react'
import { ArrowUpward, ErrorOutline } from '@mui/icons-material'
import { Button as MuiButton, CircularProgress } from '@mui/material'
import { useDispatch } from 'react-redux'
import { useOverlay } from '@global/hooks/useOverlay'
import { showSnackbar } from 'functions'
import { DragNDrop } from './dragNDrop'
import { UploadStatus } from './readyToUpload'
import * as css from './styles'
import { importStatus } from './types'
import { useFileUpload, UploadActionType } from './useFileUpload'
import { ButtonWithOverlay } from '../buttonWithOverlay'
import { t } from '@lingui/core/macro'

interface IImportButton {
  isInProgressStatus: boolean
  isFailedStatus: boolean
  fileName: string | undefined
  handleButtonClick: () => void
  disabled: boolean
  onReset: () => void
}

const ImportButton = ({
  isInProgressStatus,
  isFailedStatus,
  fileName,
  handleButtonClick,
  disabled,
  onReset,
}: IImportButton) => {
  if (!isInProgressStatus && !isFailedStatus) {
    return (
      <MuiButton
        variant="contained"
        size="small"
        disableRipple={true}
        disableElevation={true}
        css={fileName ? css.uploadButtonActiveCSS : css.uploadButtonCSS}
        onClick={handleButtonClick}
        disabled={disabled}
      >
        {fileName ?? t`Choose File`}
      </MuiButton>
    )
  }
  if (isFailedStatus) {
    return (
      <MuiButton
        variant="contained"
        size="small"
        disableRipple={true}
        disableElevation={true}
        css={css.uploadButtonCSS}
        onClick={onReset}
      >
        {t`Try again`}
      </MuiButton>
    )
  }

  return null
}

interface IList {
  listId: number
  listName: string
  listDescription: string
  listType: string
  storybookConfig?: {
    defaultStatus?: importStatus // Storybook trigger
  }
  setReadOnlyCallback: (status: boolean) => void
}

export const ImportList = ({
  listId,
  listDescription,
  listType,
  listName,
  storybookConfig = {},
  setReadOnlyCallback,
}: IList) => {
  const dispatch = useDispatch()
  const buttonRef = useRef(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { defaultStatus } = storybookConfig

  const { anchorEl, onClick, onClose: useOverlayOnClose, open, id } = useOverlay()

  // Create a wrapper function that adapts useOverlay's onClose to MUI Popover's expected signature
  const handleClose = (_event: unknown, _reason: 'backdropClick' | 'escapeKeyDown') => {
    useOverlayOnClose(null, 'close')
  }

  const { uploadFile, status, error, upload, progress, resetToDefaultStatus, dispatchFileUpload } =
    useFileUpload({
      listId: defaultStatus ? undefined : listId, // disable upload-status if Storybook is active
      defaultStatus,
      setReadOnlyCallback,
    })

  const handleFileUpload = (file: File) => {
    dispatchFileUpload({ type: UploadActionType.SET_PROGRESS, payload: { tick: 0.1 } })
    setReadOnlyCallback(true)

    upload({
      formDataFields: {
        file,
        listName,
        listType,
        type: listType,
        listId: String(listId),
        notes: listDescription,
      },
      onUploadProgress: uploadEvent => {
        // Update progress based on the progressEvent
        const currentProgress =
          typeof uploadEvent.total !== 'undefined'
            ? Math.round((uploadEvent.loaded / uploadEvent.total) * 1)
            : 0
        dispatchFileUpload({
          type: UploadActionType.SET_PROGRESS,
          payload: { tick: currentProgress, status: importStatus.UPLOADING },
        })
      },
    })

    // DEBUG ONLY: Simulate progress
    // Useful for debugging various states of the component.
    // simulateProgress(5, (tick) => {
    //   dispatchFileUpload({ type: UploadActionType.SET_PROGRESS, payload: { tick }})
    // }, () => {
    //   dispatchFileUpload({
    //     type: UploadActionType.SET_PROGRESS,
    //     payload: { tick: 1, status: importStatus.IN_PROGRESS },
    //   })
    // })
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    handleFileUpload(file)
  }

  const onDropHandler = useCallback(
    (files: File[]) => {
      if (files.length > 1) {
        dispatch(
          showSnackbar({
            type: 'ERROR',
            message: t`Uploading multiple files is not supported. Please upload one file at a time.`,
          }),
        )
        return
      }

      if (!files[0]) {
        return
      }

      handleFileUpload(files[0])
    },
    [handleFileUpload],
  )

  // Function to trigger the hidden file input click event
  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  const resetImportListState = () => {
    resetToDefaultStatus()

    // Reset the file input value
    if (!fileInputRef.current) return
    fileInputRef.current.value = ''
  }

  const isInProgressStatus = status === importStatus.IN_PROGRESS
  const isFailedStatus = status === importStatus.FAILED
  const isButtonDisabled = status !== importStatus.DEFAULT

  const statusToIconMap = {
    [importStatus.DEFAULT]: <ArrowUpward />,
    [importStatus.UPLOADING]: <CircularProgress size={20} />,
    [importStatus.IN_PROGRESS]: <CircularProgress size={20} />,
    [importStatus.FAILED]: <ErrorOutline sx={{ color: 'var(--clvn-red-300)' }} />,
  }

  if (!listId) return null

  return (
    <ButtonWithOverlay>
      <ButtonWithOverlay.Button
        popoverId="import-list"
        ref={buttonRef}
        onClick={onClick}
        v2Enabled={true}
        data-test="test-import-button"
        extendStyles={{ height: 35 }}
      >
        {statusToIconMap[status]}
        {status}
      </ButtonWithOverlay.Button>
      <ButtonWithOverlay.Overlay
        popoverProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
          open,
          onClose: handleClose,
          anchorEl,
          id,
        }}
        sx={{
          '& .MuiPaper-root': {
            width: 350,
          },
        }}
      >
        <css.Container>
          <DragNDrop onDrop={onDropHandler}>
            <UploadStatus status={status} error={error} progress={progress} />
          </DragNDrop>
          <ImportButton
            disabled={isButtonDisabled}
            fileName={uploadFile?.name}
            onReset={resetImportListState}
            isFailedStatus={isFailedStatus}
            isInProgressStatus={isInProgressStatus}
            handleButtonClick={handleButtonClick}
          />
          <input
            type="file"
            style={{ display: 'none' }} // Hide the file input
            ref={fileInputRef}
            onChange={handleFileChange}
          />
        </css.Container>
      </ButtonWithOverlay.Overlay>
    </ButtonWithOverlay>
  )
}
